import React, { useState } from 'react';
import { ConfirmModal } from '../../../commons/components/ConfirmModal';
import useApi from '../../../api/backendApiContext';
import { useQueryCacheManager } from '../../../hooks/useQueryCacheManager';
import { useMutation } from '@tanstack/react-query';
import {
  CanPauseSubscriptionDetails,
  Customer,
  INVOICE_TYPE_ISSUED,
  IssuedInvoice,
} from '../../types';
import useAnalyticsPage from '../../../web-analytics/hooks/useAnalyticsPage';
import { Page } from '../../../web-analytics/Page';
import moment from 'moment';
import { useFetchedBillingInvoices } from '../../hooks/useFetchedBillingInvoices';
import { PauseSubscriptionInvoicesModal } from '../pause/PauseSubscriptionInvoicesModal';
import { isInvoicePayable } from '../../utils';
import { useNotification } from '../../../notification/notificationsContext';
import useAnalytics from '../../../web-analytics/webAnalyticsContext';
import { analyticsTrack } from '../../../web-analytics/webAnalytics';
import { AnalyticsEvent } from '../../../web-analytics/AnalyticsEvent';

const formatMonthAndDay = (date: Date) => moment(date).format('MMMM D');

export const PauseStep = (props: {
  pauseDetails: CanPauseSubscriptionDetails;
  onNext: () => void;
  onGoBackClicked: () => void;
  onClosed: () => void;
}) => {
  useAnalyticsPage(Page.CANCELLATION_WIZARD_BENEFITS);
  const analytics = useAnalytics();

  const { pauseDetails, onNext, onGoBackClicked, onClosed } = props;
  const { billingApi } = useApi();
  const queryCacheManager = useQueryCacheManager();

  const { notifySuccess, notifyError } = useNotification();
  const { mutate: pausePlan, status: pausePlanStatus } = useMutation(
    async () => billingApi.pauseBillingPlan(),
    {
      onError: async () => {
        await queryCacheManager.invalidateBillingCustomer();
        notifyError({
          notificationMsg:
            'Failed to pause your subscription. Please try again later or contact support.',
        });
      },
      onSuccess: async (customer: Customer) => {
        await queryCacheManager.updateBillingCustomer(customer);
        notifySuccess({ notificationMsg: 'Your subscription has been paused.' });
        analyticsTrack(analytics, AnalyticsEvent.BILLING_PLAN_PAUSED);
        onClosed();
      },
    }
  );

  const [payInvoicesModalKey, setPayInvoicesModalKey] = useState<number | null>(null);
  const {
    query: { data: invoices },
  } = useFetchedBillingInvoices({ refetchOnWindowFocus: 'always' });

  const [unpaidInvoiceIds, setUnpaidInvoiceIds] = useState<string[] | null>(null);
  return (
    <ConfirmModal
      title="Pause your account"
      ariaHideApp={false}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={true}
      footer={{
        type: 'show',
        cancelButton: {
          label: 'Dismiss',
          onClick: () => {},
        },
        extraButtons: [
          {
            label: 'Go back',
            onClick: onGoBackClicked,
          },
          {
            label: 'Continue cancelation',
            variant: 'secondary',
            disabled: pausePlanStatus === 'loading' || pausePlanStatus === 'success',
            onClick: onNext,
          },
          {
            label: 'Pause for 3 months',
            variant: 'primary',
            disabled: pausePlanStatus === 'loading' || pausePlanStatus === 'success',
            onClick: () => {
              if (!invoices) return;
              const unpaidInvoices = invoices
                .filter((x) => x.type === INVOICE_TYPE_ISSUED)
                .filter(isInvoicePayable) as IssuedInvoice[];
              if (unpaidInvoices && unpaidInvoices.length > 0) {
                setUnpaidInvoiceIds(unpaidInvoices.map((inv) => inv.id));
                setPayInvoicesModalKey(new Date().valueOf());
              } else {
                pausePlan();
              }
            },
          },
        ],
      }}
    >
      <>
        <div className="cancellation-intro__description">
          <span>
            We’ll pause your subscription so you won’t receive any new invoices for up to 3 months.
            You can change your mind anytime, it is easy to resume your subscription or cancel
            through our dashboard. Please note that all outstanding invoices need to be paid before
            we can pause the account.
          </span>
          <div className="pause-step__timeline">
            <div className="pause-step__timeline-item">
              <div className="pause-step__timeline-icon pause-step__timeline-icon--active">
                <div className="pause-step__timeline-icon--active-internal" />
              </div>
              <div className="pause-step__timeline-content">
                <p className="growegy-font-em">
                  {formatMonthAndDay(pauseDetails.pauseAt)} — Your 3-month pause begins
                </p>
                <p>Your pause begins one day before your next billing date.</p>
              </div>
            </div>

            <div className="pause-step__timeline-item">
              <div className="pause-step__timeline-icon">🔔</div>
              <div className="pause-step__timeline-content">
                <p className="growegy-font-em">{formatMonthAndDay(pauseDetails.remindAt)}</p>
                <p>We’ll remind you before your pause ends.</p>
              </div>
            </div>

            <div className="pause-step__timeline-item">
              <div className="pause-step__timeline-icon">👑</div>
              <div className="pause-step__timeline-content">
                <p className="growegy-font-em">{formatMonthAndDay(pauseDetails.resumeAt)}</p>
                <p>
                  Your subscription resumes and your next invoice will automatically be sent, unless
                  you’ve already canceled or resumed your subscription early.
                </p>
              </div>
            </div>
          </div>
        </div>

        {payInvoicesModalKey && invoices && unpaidInvoiceIds && unpaidInvoiceIds.length > 0 && (
          <PauseSubscriptionInvoicesModal
            key={payInvoicesModalKey}
            invoices={
              invoices.filter(
                (inv) =>
                  inv.type === INVOICE_TYPE_ISSUED && unpaidInvoiceIds.find((id) => id === inv.id)
              ) as IssuedInvoice[]
            }
          />
        )}
      </>
    </ConfirmModal>
  );
};
